// src/features/calendar/TimezoneSelect.tsx
// Searchable timezone combobox — Intl.supportedValuesOf('timeZone') grouped by
// region, with the current UTC offset shown next to each zone. Same trigger +
// portal-dropdown pattern as UserPicker/EmojiPicker/IconPicker.

import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@core/ui';

// tsconfig targets ES2020; Intl.supportedValuesOf is ES2022 but supported by
// every browser this app targets. Augment the global Intl namespace locally
// instead of bumping the project-wide lib target.
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace -- ambient merge into the built-in Intl namespace, no ES module alternative
  namespace Intl {
    function supportedValuesOf(input: string): string[];
  }
}

function supportedTimezones(): string[] {
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('timeZone');
    }
  } catch {
    // fall through to fallback list
  }
  return [
    'UTC', 'Europe/London', 'Europe/Amsterdam', 'Europe/Berlin', 'Europe/Moscow',
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Kolkata', 'Asia/Dubai',
    'Australia/Sydney', 'Pacific/Auckland',
  ];
}

function regionOf(tz: string): string {
  const i = tz.indexOf('/');
  return i === -1 ? 'Other' : tz.slice(0, i);
}

function cityLabel(tz: string): string {
  const parts = tz.split('/');
  return parts[parts.length - 1].replace(/_/g, ' ');
}

function offsetOf(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' }).formatToParts(new Date());
    const tzPart = parts.find((p) => p.type === 'timeZoneName');
    return tzPart ? tzPart.value.replace('GMT', 'UTC') : '';
  } catch {
    return '';
  }
}

interface TimezoneOption {
  tz: string;
  region: string;
  city: string;
  offset: string;
}

let CACHED_OPTIONS: TimezoneOption[] | null = null;
function allOptions(): TimezoneOption[] {
  if (CACHED_OPTIONS) return CACHED_OPTIONS;
  CACHED_OPTIONS = supportedTimezones()
    .map((tz) => ({
      tz,
      region: regionOf(tz),
      city: cityLabel(tz),
      offset: offsetOf(tz),
    }))
    .sort((a, b) => {
      if (a.region !== b.region) return a.region.localeCompare(b.region);
      return a.city.localeCompare(b.city);
    });
  return CACHED_OPTIONS;
}

export interface TimezoneSelectProps {
  value: string;
  onChange: (tz: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  noResultsLabel?: string;
  clearLabel?: string;
  autoLabel?: string;
  'aria-label'?: string;
  className?: string;
}

export function TimezoneSelect({
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  noResultsLabel,
  clearLabel,
  autoLabel,
  'aria-label': ariaLabel,
  className,
}: TimezoneSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const options = useMemo(() => allOptions(), []);

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const maxH = 320;
    const openAbove = spaceBelow < maxH && rect.top > spaceBelow;

    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      width: Math.max(rect.width, 300),
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
      maxHeight: Math.min(maxH, openAbove ? rect.top - 8 : spaceBelow - 8),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        ref.current && !ref.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
        setSearch('');
      }
    };
    const handleScroll = () => updatePosition();
    document.addEventListener('mousedown', handler);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
    else setSearch('');
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.tz.toLowerCase().includes(q) || o.city.toLowerCase().includes(q) || o.region.toLowerCase().includes(q),
    );
  }, [options, search]);

  const grouped = useMemo(() => {
    const groups = new Map<string, TimezoneOption[]>();
    for (const o of filtered) {
      const list = groups.get(o.region) ?? [];
      list.push(o);
      groups.set(o.region, list);
    }
    return Array.from(groups.entries());
  }, [filtered]);

  const selected = options.find((o) => o.tz === value);

  const handleSelect = (tz: string) => {
    onChange(tz);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setOpen(false);
  };

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(!open)}
        aria-label={ariaLabel || placeholder}
        className={cn(
          'flex w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-left text-sm transition-colors',
          'hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          open && 'ring-2 ring-ring',
        )}
      >
        <svg className="h-4 w-4 shrink-0 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <circle cx="12" cy="12" r="9" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 2" />
        </svg>
        {selected ? (
          <span className="min-w-0 flex-1 truncate">
            {selected.city}
            <span className="ml-1.5 font-mono text-xs text-muted-foreground">{selected.offset}</span>
          </span>
        ) : (
          <span className="flex-1 truncate text-muted-foreground">{placeholder || autoLabel}</span>
        )}
        {value && (
          <svg
            onClick={handleClear}
            className="h-3.5 w-3.5 shrink-0 text-muted-foreground hover:text-foreground"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            aria-label={clearLabel}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        )}
        <svg className="h-3.5 w-3.5 shrink-0 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && createPortal(
        <div ref={dropdownRef} style={dropdownStyle} className="z-50 flex flex-col rounded-lg border border-border bg-popover shadow-lg overflow-hidden">
          <div className="border-b border-border p-2">
            <div className="relative">
              <svg className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full rounded-md border border-border bg-background py-1.5 pl-7 pr-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>
          <div className="overflow-y-auto py-1" style={{ maxHeight: 260 }}>
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-muted-foreground">{noResultsLabel}</p>
            ) : (
              grouped.map(([region, zones]) => (
                <div key={region}>
                  <p className="px-3 pb-0.5 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                    {region}
                  </p>
                  {zones.map((o) => (
                    <button
                      key={o.tz}
                      type="button"
                      onClick={() => handleSelect(o.tz)}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted',
                        o.tz === value && 'bg-accent/70',
                      )}
                    >
                      <span className="truncate">{o.city}</span>
                      <span className="shrink-0 font-mono text-xs text-muted-foreground">{o.offset}</span>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default TimezoneSelect;
